import * as Sentry from '@sentry/vue';
import { App } from 'vue';
import { Router } from 'vue-router';

function initSentry(app: App, router: Router) {
   const browserTracing = Sentry.browserTracingIntegration({ router });
   const enabled = ['acc', 'prod', 'release'].includes(import.meta.env.VITE_APP_ENVIRONMENT);

   Sentry.init({
      app,
      attachProps: true,
      attachStacktrace: true,
      dsn: import.meta.env.VITE_APP_SENTRY_DSN,
      enabled,
      environment: import.meta.env.VITE_APP_ENVIRONMENT,
      integrations: [browserTracing],
      maxBreadcrumbs: 50,
      release: import.meta.env.VITE_APP_VERSION,
      tracesSampleRate: 0.1,
      ignoreErrors: [
         'Request aborted',
         'AxiosError',
         'Unable to preload CSS',
         // Ignore dynamic import failures - these happen when users have cached old HTML after new deployments
         /Failed to fetch dynamically imported module/i,
         /error loading dynamically imported module/i,
         /dynamically imported module/i,
         // User cancelled the native share sheet - not an application error
         /AbortError.*[Ss]hare/,
         /Abort due to cancellation of share/,
         // Bridges injected by in-app browsers (Facebook, Snapchat, Instagram, ...) that the
         // page neither owns nor controls. They throw whenever those apps try to autofill
         // contact info or post messages back to the native shell.
         /window\.webkit\.messageHandlers/,
         /SCDynimacBridge/,
         /setContactAutofillValuesFromBridge/,
      ],
      denyUrls: [
         // Errors originating in scripts injected by in-app browsers - their source URL is
         // either `<anonymous>` or a custom bridge scheme rather than our app bundle.
         /iabjs:\/\//i,
         /JSBridgeCallback/i,
      ],
      beforeSend(event) {
         // Additional filter for dynamic import and service worker errors
         if (event.exception?.values?.[0]?.value) {
            const errorMessage = event.exception.values[0].value.toLowerCase();

            // Ignore dynamic import failures (deployment/caching issues, not code bugs)
            if (
               errorMessage.includes('dynamically imported module') ||
               errorMessage.includes('failed to fetch') ||
               errorMessage.includes('dynamic import')
            ) {
               return null;
            }

            // Ignore service worker script load failures (infrastructure issues)
            if (errorMessage.includes('script') && errorMessage.includes('sw.js') && errorMessage.includes('load failed')) {
               return null;
            }
         }

         return event;
      },
   });
}

export { initSentry };

